import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { ChatExpandIcon } from './chat-drawer-icons.tsx';
import { WundoChatIcon } from './wundo-chat-icon.tsx';

interface ChatDrawerStripProps {
  open: boolean;
  label: string;
  onExpand: () => void;
  stripRef?: React.Ref<HTMLButtonElement>;
  unread: boolean;
  unreadLabel?: string;
}

/**
 * What is left of the collapsed drawer on a desktop, and the way back in. The
 * mark says which panel is parked there, the chevron says it opens — both
 * always drawn, because an affordance that only appears on hover does not
 * exist for anyone who cannot hover. The whole strip is the hit area.
 */
export function ChatDrawerStrip({
  open,
  label,
  onExpand,
  stripRef,
  unread,
  unreadLabel,
}: ChatDrawerStripProps) {
  return (
    <>
      <button
        ref={stripRef}
        data-slot='chat-strip'
        type='button'
        aria-label={label}
        aria-expanded={open}
        onClick={onExpand}
        className={cn(
          open ? 'hidden' : 'flex',
          'absolute inset-y-0 left-0 z-10 w-11 flex-col items-center gap-2.5 pt-9',
          'cursor-pointer text-ink transition-colors hover:bg-fill-faint',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
        )}
      >
        <WundoChatIcon size={20} />
        <ChatExpandIcon size={14} className='text-ink-faint' />
        {/*
          Something came in while the drawer was away. A dot rather than a
          count: what is waiting is a reply, and how many is not the question.
        */}
        {unread ? (
          <span
            data-slot='chat-strip-dot'
            aria-hidden='true'
            className='absolute top-8.25 right-1.75 size-1.75 rounded-full bg-accent'
          />
        ) : null}
      </button>
      {/*
        And said out loud, because a green dot is not an announcement. Beside
        the strip rather than inside it: the strip carries a label, and a
        label is what a screen reader reads instead of the contents.
      */}
      {unread && !open && unreadLabel ? (
        <span role='status' className='sr-only'>
          {unreadLabel}
        </span>
      ) : null}
    </>
  );
}
